export type AiRedactionPolicy = 'STRICT_CLEAN_ROOM' | 'ALLOW_PII';

type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };

type AnonymizeResult<T> = {
  output: T;
  meta: {
    policy: AiRedactionPolicy;
    pseudonymByKey: Record<string, string>;
  };
};

const NAME_KEYS = new Set(['personName', 'name', 'fullName', 'employeeName'].map((k) => k.toLowerCase()));
const ID_KEYS = new Set(
  ['email', 'employeeId', 'employee_id', 'personId', 'person_id', 'ssn', 'phone', 'phoneNumber'].map((k) =>
    k.toLowerCase(),
  ),
);
const TEXT_KEYS = new Set(['resumeSummary', 'notes', 'lastReviewSummary'].map((k) => k.toLowerCase()));
const COST_KEYS = new Set(['annualCost', 'fullyLoadedAnnualCost', 'compAnnual', 'salary', 'bonus', 'totalComp'].map((k) =>
  k.toLowerCase(),
));

function normalizeToken(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const token = value.trim();
  return token ? token : null;
}

function identityKeyForObject(value: Record<string, unknown>): string | null {
  const keyByLower = new Map<string, string>();
  for (const k of Object.keys(value)) keyByLower.set(k.toLowerCase(), k);

  const getValue = (key: string): unknown => {
    const actualKey = keyByLower.get(key.toLowerCase());
    return actualKey ? (value as any)[actualKey] : undefined;
  };

  const personId = normalizeToken(getValue('personId')) ?? normalizeToken(getValue('person_id'));
  if (personId) return `person:${personId}`;
  const positionId = normalizeToken(getValue('positionId'));
  if (positionId) return `position:${positionId}`;
  const email = normalizeToken(getValue('email'));
  if (email) return `email:${email.toLowerCase()}`;
  return null;
}

function getPseudonym(state: { map: Map<string, string>; next: number }, key: string | null): string {
  if (!key) return `P${state.next++}`;
  const existing = state.map.get(key);
  if (existing) return existing;
  const value = `P${state.next++}`;
  state.map.set(key, value);
  return value;
}

function anonymizeJson(value: JsonValue, policy: AiRedactionPolicy, state: { map: Map<string, string>; next: number }): JsonValue {
  if (policy === 'ALLOW_PII') return value;

  if (value == null) return value;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return value;

  if (Array.isArray(value)) {
    return value.map((item) => anonymizeJson(item, policy, state));
  }

  const identityKey = identityKeyForObject(value as any);
  const output: Record<string, JsonValue> = {};

  for (const [k, v] of Object.entries(value)) {
    const kNorm = k.toLowerCase();

    if (NAME_KEYS.has(kNorm)) {
      output[k] = getPseudonym(state, identityKey);
      continue;
    }

    if (ID_KEYS.has(kNorm)) {
      output[k] = null;
      continue;
    }

    if (TEXT_KEYS.has(kNorm)) {
      output[k] = null;
      continue;
    }

    if (COST_KEYS.has(kNorm)) {
      output[k] = null;
      continue;
    }

    output[k] = anonymizeJson(v as JsonValue, policy, state);
  }

  return output;
}

export function anonymizeForAiPrompt<T>(input: T, policy: AiRedactionPolicy): AnonymizeResult<T> {
  if (policy === 'ALLOW_PII') {
    return { output: input, meta: { policy, pseudonymByKey: {} } };
  }

  const state = { map: new Map<string, string>(), next: 1 };
  const output = anonymizeJson(input as unknown as JsonValue, policy, state) as unknown as T;
  return { output, meta: { policy, pseudonymByKey: Object.fromEntries(state.map.entries()) } };
}
